<template>
  <div class="ef-wrapper">
    <!-- ── Cabecera ── -->
    <div class="ef-topbar">
      <button
        class="ef-back-btn"
        :disabled="processing"
        @click="goBack"
      >
        <i class="fa fa-arrow-left" />
        <span>Volver</span>
      </button>
      <div class="ef-topbar-title">
        <h2>Nueva Ejecución en Tienda</h2>
        <p>Define el foco para que los supervisores respondan desde la app</p>
      </div>
    </div>

    <!-- ── Stepper ── -->
    <div class="ef-stepper">
      <div
        v-for="(step, i) in steps"
        :key="i"
        class="ef-step"
        :class="{ active: currentStep === i, done: currentStep > i }"
        @click="currentStep > i && goToStep(i)"
      >
        <div class="ef-step-bubble">
          <i
            v-if="currentStep > i"
            class="fa fa-check"
          />
          <span v-else>{{ i + 1 }}</span>
        </div>
        <div class="ef-step-info">
          <span class="ef-step-num">Paso {{ i + 1 }}</span>
          <span class="ef-step-label">{{ step.label }}</span>
        </div>
        <div
          v-if="i < steps.length - 1"
          class="ef-step-line"
          :class="{ filled: currentStep > i }"
        />
      </div>
    </div>

    <!-- ── Contenido ── -->
    <div class="ef-card">
      <!-- PASO 1 · Información general -->
      <transition
        name="slide-fade"
        mode="out-in"
      >
        <div
          v-if="currentStep === 0"
          key="s0"
        >
          <div class="ef-card-header">
            <div
              class="ef-card-icon"
              style="background:#ebf8ff;color:#3182ce"
            >
              <i class="fa fa-info-circle" />
            </div>
            <div>
              <h3 class="ef-card-title">
                Información General
              </h3>
              <p class="ef-card-sub">
                Define nombre, vigencia y estado de la ejecución
              </p>
            </div>
          </div>
          <div class="ef-card-body">
            <div class="ef-field">
              <label class="ef-label">Nombre <span class="req">*</span></label>
              <input
                v-model="form.name"
                class="ef-input"
                :class="{ 'ef-input--error': submitted0 && !form.name.trim() }"
                placeholder="Ej: Campaña verano 2026"
                type="text"
              >
              <p
                v-if="submitted0 && !form.name.trim()"
                class="ef-error"
              >
                El nombre es obligatorio
              </p>
            </div>

            <div class="ef-field">
              <label class="ef-label">Descripción <span class="ef-optional">(opcional)</span></label>
              <textarea
                v-model="form.description"
                class="ef-input ef-textarea"
                placeholder="Describe el objetivo de esta ejecución"
                rows="3"
              />
            </div>

            <div class="ef-row">
              <div class="ef-field ef-field--half">
                <label class="ef-label">Fecha inicio <span class="req">*</span></label>
                <input
                  v-model="form.start_date"
                  class="ef-input"
                  :class="{ 'ef-input--error': submitted0 && !form.start_date }"
                  type="date"
                >
                <p
                  v-if="submitted0 && !form.start_date"
                  class="ef-error"
                >
                  La fecha de inicio es obligatoria
                </p>
              </div>
              <div class="ef-field ef-field--half">
                <label class="ef-label">Fecha fin <span class="ef-optional">(opcional)</span></label>
                <input
                  v-model="form.end_date"
                  class="ef-input"
                  :class="{ 'ef-input--error': endDateError }"
                  type="date"
                  :min="form.start_date || undefined"
                >
                <p
                  v-if="endDateError"
                  class="ef-error"
                >
                  Debe ser posterior a la fecha de inicio
                </p>
              </div>
            </div>
          </div>
        </div>

        <!-- PASO 2 · Tiendas -->
        <div
          v-else-if="currentStep === 1"
          key="s1"
        >
          <div class="ef-card-header">
            <div
              class="ef-card-icon"
              style="background:#f0fff4;color:#276749"
            >
              <i class="fa fa-store" />
            </div>
            <div>
              <h3 class="ef-card-title">
                Tiendas
              </h3>
              <p class="ef-card-sub">
                Agrega las tiendas donde se aplicará esta ejecución <span class="ef-optional">(opcional)</span>
              </p>
            </div>
          </div>
          <div class="ef-card-body">
            <div class="ef-store-row">
              <input
                v-model.number="storeIdInput"
                class="ef-input ef-store-input"
                type="number"
                placeholder="ID de tienda"
                @keydown.enter.prevent="addStoreId"
              >
              <button
                class="ef-add-btn"
                :disabled="!storeIdInput"
                @click="addStoreId"
              >
                <i class="fa fa-plus" />
                Agregar
              </button>
            </div>
            <p
              v-if="storeDuplicateError"
              class="ef-error"
            >
              Esa tienda ya está en la lista
            </p>

            <div
              v-if="form.store_ids.length"
              class="ef-tags"
            >
              <span
                v-for="id in form.store_ids"
                :key="id"
                class="ef-tag"
              >
                <i class="fa fa-store ef-tag-icon" />
                {{ id }}
                <button
                  class="ef-tag-remove"
                  @click="removeStoreId(id)"
                >
                  <i class="fa fa-times" />
                </button>
              </span>
            </div>

            <div
              v-else
              class="ef-empty-state"
            >
              <i class="fa fa-store ef-empty-icon" />
              <p>Sin tiendas asignadas</p>
              <span>Si no agregas tiendas, aplica para todas</span>
            </div>
          </div>
        </div>

        <!-- PASO 3 · Productos y reglas -->
        <div
          v-else-if="currentStep === 2"
          key="s2"
        >
          <div class="ef-card-header">
            <div
              class="ef-card-icon"
              style="background:#faf5ff;color:#6b46c1"
            >
              <i class="fa fa-box" />
            </div>
            <div>
              <h3 class="ef-card-title">
                Productos & Reglas
              </h3>
              <p class="ef-card-sub">
                Busca productos y define qué debe verificar el supervisor <span class="ef-optional">(opcional)</span>
              </p>
            </div>
          </div>
          <div class="ef-card-body">
            <!-- Buscador -->
            <div class="ef-product-search">
              <i
                class="fa ef-search-icon"
                :class="globalSearchLoading ? 'fa-circle-o-notch ef-search-icon--spin' : 'fa-search'"
              />
              <input
                v-model="globalSearchQuery"
                class="ef-input ef-search-input"
                :class="{ 'ef-search-input--active': dropdownOpen && globalSearchQuery.length >= 2 }"
                placeholder="Buscar producto por nombre o SKU..."
                @input="onProductSearchInput"
                @blur="closeDropdownSoon"
              >
              <div
                v-if="dropdownOpen && globalSearchQuery.length >= 2"
                class="ef-dropdown"
              >
                <!-- Cargando -->
                <div
                  v-if="globalSearchLoading"
                  class="ef-dropdown-loading"
                >
                  <div class="ef-skeleton-row" />
                  <div class="ef-skeleton-row ef-skeleton-row--sm" />
                  <div class="ef-skeleton-row" />
                  <div class="ef-skeleton-row ef-skeleton-row--sm" />
                </div>

                <!-- Sin resultados -->
                <div
                  v-else-if="!globalSearchResults.length"
                  class="ef-dropdown-empty"
                >
                  <i class="fa fa-search-minus" />
                  Sin resultados para "{{ globalSearchQuery }}"
                </div>

                <!-- Resultados -->
                <template v-else>
                  <button
                    v-for="prod in globalSearchResults"
                    :key="prod.product_id"
                    class="ef-dropdown-item"
                    :class="{ added: form.products.some(p => p.product_id === prod.product_id) }"
                    @mousedown.prevent="addProduct(prod)"
                  >
                    <div class="ef-dropdown-name">
                      {{ prod.description || prod.business_key }}
                    </div>
                    <div class="ef-dropdown-meta">
                      <span>{{ prod.business_key }}</span>
                      <span class="ef-dot">·</span>
                      <span>ID {{ prod.product_id }}</span>
                      <span
                        v-if="form.products.some(p => p.product_id === prod.product_id)"
                        class="ef-already-badge"
                      >Agregado</span>
                    </div>
                  </button>
                </template>
              </div>
            </div>

            <!-- Lista de productos -->
            <div
              v-if="!form.products.length"
              class="ef-empty-state"
            >
              <i class="fa fa-box ef-empty-icon" />
              <p>Sin productos asignados</p>
              <span>Busca y agrega productos usando el campo de arriba</span>
            </div>

            <div
              v-for="(product, pIdx) in form.products"
              :key="product.product_id"
              class="ef-product"
            >
              <!-- Header producto -->
              <div class="ef-product-head">
                <div class="ef-product-avatar">
                  <i class="fa fa-box" />
                </div>
                <div class="ef-product-info">
                  <span class="ef-product-name">{{ product.product_name }}</span>
                  <span class="ef-product-id">ID {{ product.product_id }}</span>
                </div>
                <button
                  class="ef-icon-btn ef-icon-btn--danger"
                  title="Quitar producto"
                  @click="removeProduct(pIdx)"
                >
                  <i class="fa fa-trash" />
                </button>
              </div>

              <!-- Reglas -->
              <div class="ef-rules">
                <div class="ef-rules-title">
                  <span>Reglas de verificación</span>
                  <button
                    class="ef-ghost-btn"
                    @click="addRule(pIdx)"
                  >
                    <i class="fa fa-plus" />
                    Agregar regla
                  </button>
                </div>

                <div
                  v-if="!product.rules.length"
                  class="ef-rules-empty"
                >
                  Sin reglas definidas — la ejecución sólo registrará la visita
                </div>

                <div
                  v-for="(rule, rIdx) in product.rules"
                  :key="rIdx"
                  class="ef-rule"
                >
                  <div class="ef-rule-head">
                    <span class="ef-rule-num">Regla {{ rIdx + 1 }}</span>
                    <button
                      class="ef-icon-btn ef-icon-btn--ghost"
                      @click="removeRule(pIdx, rIdx)"
                    >
                      <i class="fa fa-times" />
                    </button>
                  </div>

                  <div class="ef-rule-body">
                    <div class="ef-field">
                      <label class="ef-label">Tipo de verificación <span class="req">*</span></label>
                      <select
                        v-model="rule.rule_type_id"
                        class="ef-select"
                        :class="{ 'ef-input--error': submitted2 && !rule.rule_type_id }"
                        :disabled="ruleTypesLoading"
                        @change="onRuleTypeChange(pIdx, rIdx)"
                      >
                        <option
                          :value="null"
                          disabled
                        >
                          {{ ruleTypesLoading ? 'Cargando tipos...' : '-- Selecciona un tipo --' }}
                        </option>
                        <option
                          v-for="opt in ruleCodeOptions"
                          :key="opt.value"
                          :value="opt.value"
                          :disabled="product.rules.filter((r, i) => i !== rIdx).some(r => r.rule_type_id === opt.value)"
                        >
                          {{ opt.text }}
                        </option>
                      </select>
                      <p
                        v-if="submitted2 && !rule.rule_type_id"
                        class="ef-error"
                      >
                        Selecciona un tipo
                      </p>
                    </div>

                    <div class="ef-field">
                      <label class="ef-label">Pregunta para el supervisor <span class="ef-optional">(opcional)</span></label>
                      <textarea
                        v-model="rule.question_label"
                        class="ef-input ef-textarea ef-textarea--sm"
                        placeholder="Ej: ¿El producto está en el exhibidor principal?"
                        rows="2"
                        maxlength="255"
                      />
                      <span class="ef-char-count">{{ rule.question_label ? rule.question_label.length : 0 }}/255</span>
                    </div>

                    <div class="ef-rule-checks">
                      <label class="ef-check-label">
                        <input
                          v-model="rule.is_required"
                          type="checkbox"
                          class="ef-check"
                        >
                        <span>Obligatoria</span>
                      </label>
                      <label class="ef-check-label">
                        <input
                          v-model="rule.allow_comment"
                          type="checkbox"
                          class="ef-check"
                        >
                        <span>Permitir comentario</span>
                      </label>
                    </div>
                  </div>
                </div>

                <p
                  v-if="productDuplicateRuleError(pIdx)"
                  class="ef-error mt-1"
                >
                  <i class="fa fa-exclamation-circle" /> Hay reglas con tipo duplicado en este producto
                </p>
              </div>
            </div>

            <p
              v-if="productDuplicateIdError"
              class="ef-error"
            >
              <i class="fa fa-exclamation-circle" /> Hay productos duplicados
            </p>
          </div>
        </div>

        <!-- PASO 4 · Revisión -->
        <div
          v-else
          key="s3"
        >
          <div class="ef-card-header">
            <div
              class="ef-card-icon"
              style="background:#fffaf0;color:#c05621"
            >
              <i class="fa fa-clipboard-check" />
            </div>
            <div>
              <h3 class="ef-card-title">
                Revisión Final
              </h3>
              <p class="ef-card-sub">
                Confirma los datos antes de crear la ejecución
              </p>
            </div>
          </div>
          <div class="ef-card-body">
            <!-- Info general -->
            <div class="ef-review-section">
              <div
                class="ef-review-section-head"
                @click="goToStep(0)"
              >
                <span><i class="fa fa-info-circle" /> Información General</span>
                <span class="ef-review-edit"><i class="fa fa-pencil" /> Editar</span>
              </div>
              <div class="ef-review-grid">
                <div class="ef-review-field">
                  <span class="ef-review-key">Nombre</span>
                  <span class="ef-review-val">{{ form.name }}</span>
                </div>

                <div class="ef-review-field">
                  <span class="ef-review-key">Fecha inicio</span>
                  <span class="ef-review-val">{{ formatDate(form.start_date) }}</span>
                </div>
                <div class="ef-review-field">
                  <span class="ef-review-key">Fecha fin</span>
                  <span class="ef-review-val">{{ form.end_date ? formatDate(form.end_date) : '—' }}</span>
                </div>
                <div
                  v-if="form.description"
                  class="ef-review-field ef-review-field--full"
                >
                  <span class="ef-review-key">Descripción</span>
                  <span class="ef-review-val">{{ form.description }}</span>
                </div>
              </div>
            </div>

            <!-- Tiendas -->
            <div class="ef-review-section">
              <div
                class="ef-review-section-head"
                @click="goToStep(1)"
              >
                <span><i class="fa fa-store" /> Tiendas ({{ form.store_ids.length }})</span>
                <span class="ef-review-edit"><i class="fa fa-pencil" /> Editar</span>
              </div>
              <div
                v-if="form.store_ids.length"
                class="ef-tags ef-tags--compact"
              >
                <span
                  v-for="id in form.store_ids"
                  :key="id"
                  class="ef-tag"
                >{{ id }}</span>
              </div>
              <p
                v-else
                class="ef-review-empty"
              >
                Sin tiendas — aplica para todas
              </p>
            </div>

            <!-- Productos -->
            <div class="ef-review-section">
              <div
                class="ef-review-section-head"
                @click="goToStep(2)"
              >
                <span><i class="fa fa-box" /> Productos ({{ form.products.length }})</span>
                <span class="ef-review-edit"><i class="fa fa-pencil" /> Editar</span>
              </div>
              <div v-if="form.products.length">
                <div
                  v-for="p in form.products"
                  :key="p.product_id"
                  class="ef-review-product"
                >
                  <div class="ef-review-product-name">
                    <i
                      class="fa fa-box"
                      style="color:#805ad5;margin-right:6px"
                    />
                    {{ p.product_name }}
                    <span class="ef-product-id">ID {{ p.product_id }}</span>
                  </div>
                  <div
                    v-if="p.rules.length"
                    class="ef-review-rules"
                  >
                    <span
                      v-for="r in p.rules"
                      :key="r.rule_type_id"
                      class="ef-rule-chip"
                    >
                      {{ ruleLabel(r.rule_type_id) }}
                      <span
                        v-if="r.is_required"
                        class="ef-rule-req"
                      >!</span>
                    </span>
                  </div>
                  <p
                    v-else
                    class="ef-rules-empty"
                  >
                    Sin reglas
                  </p>
                </div>
              </div>
              <p
                v-else
                class="ef-review-empty"
              >
                Sin productos asignados
              </p>
            </div>

            <!-- Botón crear -->
            <button
              class="ef-create-btn"
              :disabled="processing"
              @click="handleSubmit"
            >
              <b-spinner
                v-if="processing"
                small
                style="margin-right:8px"
              />
              <i
                v-else
                class="fa fa-rocket"
                style="margin-right:8px"
              />
              {{ processing ? 'Creando ejecución...' : 'Crear Ejecución' }}
            </button>
          </div>
        </div>
      </transition>
    </div>

    <!-- ── Navegación ── -->
    <div class="ef-nav">
      <button
        v-if="currentStep > 0"
        class="ef-nav-btn ef-nav-btn--secondary"
        @click="prevStep"
      >
        <i class="fa fa-chevron-left" />
        Anterior
      </button>
      <div v-else />
      <button
        v-if="currentStep < 3"
        class="ef-nav-btn ef-nav-btn--primary"
        @click="nextStep"
      >
        Siguiente
        <i class="fa fa-chevron-right" />
      </button>
    </div>
  </div>
</template>

<script>
import { ref, computed, onMounted } from '@vue/composition-api'
import { createExecutionFocus, getExecutionFocusRuleTypes } from '../Services'
import ExecutionFocusRuleType from '../models/ExecutionFocusRuleType'
import { searchProducts } from '@/app/product_master/Services'

function makeRule () {
  return { rule_type_id: null, is_required: true, allow_comment: false, question_label: '' }
}

export default {
  name: 'CreateExecutionFocus',
  setup (_, context) {
    const processing = ref(false)
    const currentStep = ref(0)
    const submitted0 = ref(false)
    const submitted2 = ref(false)

    const storeIdInput = ref(null)
    const storeDuplicateError = ref(false)

    const globalSearchQuery = ref('')
    const globalSearchResults = ref([])
    const globalSearchLoading = ref(false)
    const dropdownOpen = ref(false)
    let globalSearchTimer = null

    const steps = [
      { label: 'Info General' },
      { label: 'Tiendas' },
      { label: 'Productos' },
      { label: 'Revisión' }
    ]

    const form = ref({
      name: '',
      description: '',
      start_date: '',
      end_date: '',
      active: true,
      store_ids: [],
      products: []
    })

    const ruleTypes = ref([])
    const ruleTypesLoading = ref(false)

    const ruleCodeOptions = computed(() =>
      ruleTypes.value.map(rt => ({ value: rt.id, text: rt.name }))
    )

    const ruleLabelMap = computed(() =>
      Object.fromEntries(ruleTypes.value.map(rt => [rt.id, rt.name]))
    )

    // Validaciones
    const endDateError = computed(() =>
      !!(form.value.end_date && form.value.start_date && form.value.end_date <= form.value.start_date)
    )

    const productDuplicateIdError = computed(() => {
      const ids = form.value.products.map(p => p.product_id).filter(Boolean)
      return ids.length !== new Set(ids).size
    })

    const productDuplicateRuleError = (pIdx) => {
      const rules = form.value.products[pIdx].rules.map(r => r.rule_type_id).filter(Boolean)
      return rules.length !== new Set(rules).size
    }

    // Stepper
    const goToStep = (i) => { currentStep.value = i }

    const nextStep = () => {
      if (currentStep.value === 0) {
        submitted0.value = true
        if (!form.value.name.trim() || !form.value.start_date || endDateError.value) return
      }
      if (currentStep.value === 2) {
        submitted2.value = true
        for (let pIdx = 0; pIdx < form.value.products.length; pIdx++) {
          if (productDuplicateRuleError(pIdx)) return
          for (const rule of form.value.products[pIdx].rules) {
            if (!rule.rule_type_id) return
          }
        }
        if (productDuplicateIdError.value) return
      }
      currentStep.value++
    }

    const prevStep = () => { currentStep.value-- }

    // Tiendas
    const addStoreId = () => {
      storeDuplicateError.value = false
      const id = Number(storeIdInput.value)
      if (!id) return
      if (form.value.store_ids.includes(id)) { storeDuplicateError.value = true; return }
      form.value.store_ids.push(id)
      storeIdInput.value = null
    }

    const removeStoreId = (id) => { form.value.store_ids = form.value.store_ids.filter(s => s !== id) }

    // Productos
    const onProductSearchInput = () => {
      dropdownOpen.value = true
      clearTimeout(globalSearchTimer)
      if (globalSearchQuery.value.length < 2) { globalSearchResults.value = []; return }
      globalSearchLoading.value = true
      globalSearchTimer = setTimeout(async () => {
        try {
          const results = await searchProducts(globalSearchQuery.value)
          globalSearchResults.value = Array.isArray(results) ? results : []
        } catch {
          globalSearchResults.value = []
        } finally {
          globalSearchLoading.value = false
        }
      }, 350)
    }

    const closeDropdownSoon = () => setTimeout(() => { dropdownOpen.value = false }, 200)

    const addProduct = (prod) => {
      if (form.value.products.some(p => p.product_id === prod.product_id)) return
      form.value.products.push({
        product_id: prod.product_id,
        product_name: prod.description || prod.business_key,
        rules: []
      })
      globalSearchQuery.value = ''
      globalSearchResults.value = []
      dropdownOpen.value = false
    }

    const removeProduct = (pIdx) => { form.value.products.splice(pIdx, 1) }

    // Reglas
    const addRule = (pIdx) => form.value.products[pIdx].rules.push(makeRule())
    const removeRule = (pIdx, rIdx) => form.value.products[pIdx].rules.splice(rIdx, 1)

    // Helpers
    const onRuleTypeChange = (pIdx, rIdx) => {
      const rule = form.value.products[pIdx].rules[rIdx]
      const rt = ruleTypes.value.find(t => t.id === rule.rule_type_id)
      if (rt && rt.description) rule.question_label = rt.description
    }

    const ruleLabel = (code) => ruleLabelMap.value[code] || code

    const formatDate = (d) => {
      if (!d) return '—'
      const [y, m, day] = d.split('-')
      return `${day}/${m}/${y}`
    }

    // Submit
    const buildPayload = () => ({
      name: form.value.name.trim(),
      active: form.value.active,
      start_date: form.value.start_date,
      description: form.value.description.trim() || null,
      end_date: form.value.end_date || null,
      store_ids: [...new Set(form.value.store_ids)],
      products: form.value.products.map((p, pIdx) => ({
        product_id: p.product_id,
        sort_order: pIdx + 1,
        rules: p.rules.map((r, rIdx) => ({
          rule_type_id: r.rule_type_id,
          is_required: r.is_required,
          allow_comment: r.allow_comment,
          question_label: r.question_label.trim() || null,
          sort_order: rIdx + 1
        }))
      }))
    })

    const makeToast = (body, title, variant = 'danger') => {
      context.root.$bvToast.toast(body, { title, autoHideDelay: 5000, appendToast: false, solid: true, variant })
    }

    const handleSubmit = async () => {
      if (processing.value) return
      processing.value = true
      try {
        const result = await createExecutionFocus(buildPayload())
        makeToast(`Ejecución creada correctamente (ID: ${result.id})`, 'Éxito', 'success')
        setTimeout(() => { context.root.$router.push({ name: 'ExecutionFocusList' }) }, 1200)
      } catch (err) {
        makeToast(err.message || 'Error al crear la ejecución', 'Error')
      } finally {
        processing.value = false
      }
    }

    const goBack = () => context.root.$router.go(-1)

    onMounted(async () => {
      ruleTypesLoading.value = true
      try {
        const raw = await getExecutionFocusRuleTypes()
        ruleTypes.value = Array.isArray(raw) ? raw.map(r => new ExecutionFocusRuleType(r)) : []
      } catch {
        makeToast('No se pudieron cargar los tipos de verificación', 'Error')
      } finally {
        ruleTypesLoading.value = false
      }
    })

    return {
      form,
      processing,
      currentStep,
      steps,
      submitted0,
      submitted2,
      storeIdInput,
      storeDuplicateError,
      globalSearchQuery,
      globalSearchResults,
      globalSearchLoading,
      dropdownOpen,
      ruleTypesLoading,
      ruleCodeOptions,
      endDateError,
      productDuplicateIdError,
      productDuplicateRuleError,
      ruleLabel,
      formatDate,
      goToStep,
      nextStep,
      prevStep,
      addStoreId,
      removeStoreId,
      addProduct,
      removeProduct,
      onProductSearchInput,
      closeDropdownSoon,
      addRule,
      removeRule,
      onRuleTypeChange,
      handleSubmit,
      goBack
    }
  }
}
</script>

<style scoped>
/* ── Variables & reset ── */
.ef-wrapper {
  --blue: #3182ce;
  --blue-light: #ebf8ff;
  --purple: #6b46c1;
  --purple-light: #faf5ff;
  --green: #276749;
  --green-light: #f0fff4;
  --gray-50: #f8fafc;
  --gray-100: #edf2f7;
  --gray-200: #e2e8f0;
  --gray-400: #a0aec0;
  --gray-600: #718096;
  --gray-800: #2d3748;
  --radius: 12px;
  --shadow: 0 4px 24px rgba(0,0,0,.08);
  padding: 2rem 1rem 3rem;
  max-width: 760px;
  margin: 0 auto;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
}

/* ── Topbar ── */
.ef-topbar {
  display: flex;
  align-items: center;
  gap: 1rem;
  margin-bottom: 2rem;
}

.ef-back-btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  background: white;
  border: 1px solid var(--gray-200);
  border-radius: 8px;
  padding: 8px 14px;
  font-size: .875rem;
  color: var(--gray-600);
  cursor: pointer;
  transition: all .15s;
  white-space: nowrap;
  flex-shrink: 0;
}
.ef-back-btn:hover:not(:disabled) { border-color: var(--blue); color: var(--blue); }
.ef-back-btn:disabled { opacity: .5; cursor: not-allowed; }

.ef-topbar-title h2 {
  margin: 0;
  font-size: 1.5rem;
  font-weight: 700;
  color: var(--gray-800);
  line-height: 1.2;
}
.ef-topbar-title p {
  margin: 4px 0 0;
  font-size: .875rem;
  color: var(--gray-600);
}

/* ── Stepper ── */
.ef-stepper {
  display: flex;
  align-items: center;
  margin-bottom: 2rem;
  background: white;
  border-radius: var(--radius);
  padding: 1.25rem 1.5rem;
  box-shadow: var(--shadow);
  overflow-x: auto;
}

.ef-step {
  display: flex;
  align-items: center;
  flex: 1;
  min-width: 0;
  position: relative;
}

.ef-step-bubble {
  width: 36px;
  height: 36px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 700;
  font-size: .85rem;
  flex-shrink: 0;
  background: var(--gray-100);
  color: var(--gray-400);
  border: 2px solid var(--gray-200);
  transition: all .3s;
}
.ef-step.active .ef-step-bubble {
  background: var(--blue);
  color: white;
  border-color: var(--blue);
  box-shadow: 0 0 0 4px rgba(49,130,206,.2);
}
.ef-step.done .ef-step-bubble {
  background: #48bb78;
  color: white;
  border-color: #48bb78;
  cursor: pointer;
}

.ef-step-info {
  display: flex;
  flex-direction: column;
  margin-left: 10px;
  min-width: 0;
  overflow: hidden;
}
.ef-step-num {
  font-size: .7rem;
  color: var(--gray-400);
  text-transform: uppercase;
  letter-spacing: .04em;
}
.ef-step-label {
  font-size: .825rem;
  font-weight: 600;
  color: var(--gray-600);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.ef-step.active .ef-step-label { color: var(--blue); }
.ef-step.done .ef-step-label { color: #48bb78; }

.ef-step-line {
  flex: 1;
  height: 2px;
  background: var(--gray-200);
  margin: 0 12px;
  border-radius: 2px;
  transition: background .3s;
}
.ef-step-line.filled { background: #48bb78; }

/* ── Card ── */
.ef-card {
  background: white;
  border-radius: var(--radius);
  box-shadow: var(--shadow);
  overflow: hidden;
}

.ef-card-header {
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 1.5rem;
  border-bottom: 1px solid var(--gray-100);
  background: var(--gray-50);
}

.ef-card-icon {
  width: 48px;
  height: 48px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.25rem;
  flex-shrink: 0;
}

.ef-card-title {
  margin: 0;
  font-size: 1.1rem;
  font-weight: 700;
  color: var(--gray-800);
}
.ef-card-sub {
  margin: 4px 0 0;
  font-size: .825rem;
  color: var(--gray-600);
}

.ef-card-body {
  padding: 1.5rem;
}

/* ── Fields ── */
.ef-field { margin-bottom: 1.25rem; }
.ef-field:last-child { margin-bottom: 0; }

.ef-label {
  display: block;
  font-size: .825rem;
  font-weight: 600;
  color: var(--gray-800);
  margin-bottom: 6px;
}
.ef-optional { font-weight: 400; color: var(--gray-400); margin-left: 4px; }
.req { color: #e53e3e; margin-left: 2px; }

.ef-input {
  width: 100%;
  border: 1.5px solid var(--gray-200);
  border-radius: 8px;
  padding: 10px 14px;
  font-size: .9rem;
  color: var(--gray-800);
  background: white;
  transition: border-color .15s, box-shadow .15s;
  box-sizing: border-box;
  outline: none;
  font-family: inherit;
}
.ef-input:focus {
  border-color: var(--blue);
  box-shadow: 0 0 0 3px rgba(49,130,206,.12);
}
.ef-input--error {
  border-color: #e53e3e;
  box-shadow: 0 0 0 3px rgba(229,62,62,.1);
}

.ef-textarea { resize: vertical; min-height: 80px; }
.ef-textarea--sm { min-height: 60px; }

.ef-select {
  width: 100%;
  border: 1.5px solid var(--gray-200);
  border-radius: 8px;
  padding: 10px 14px;
  font-size: .9rem;
  color: var(--gray-800);
  background: white;
  transition: border-color .15s;
  outline: none;
  appearance: none;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='8' viewBox='0 0 12 8'%3E%3Cpath d='M1 1l5 5 5-5' stroke='%23718096' stroke-width='1.5' fill='none' stroke-linecap='round'/%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: right 12px center;
  padding-right: 36px;
  cursor: pointer;
}
.ef-select:focus { border-color: var(--blue); box-shadow: 0 0 0 3px rgba(49,130,206,.12); }

.ef-row { display: flex; gap: 1rem; }
.ef-field--half { flex: 1; margin-bottom: 0; }

/* ── Stores ── */
.ef-store-row { display: flex; gap: .75rem; align-items: flex-end; margin-bottom: .75rem; }
.ef-store-input { max-width: 200px; flex-shrink: 0; }

.ef-add-btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 10px 16px;
  border-radius: 8px;
  border: 1.5px solid var(--blue);
  color: var(--blue);
  background: white;
  font-size: .875rem;
  font-weight: 600;
  cursor: pointer;
  transition: all .15s;
  white-space: nowrap;
}
.ef-add-btn:hover:not(:disabled) { background: var(--blue-light); }
.ef-add-btn:disabled { opacity: .45; cursor: not-allowed; }

/* ── Tags ── */
.ef-tags { display: flex; flex-wrap: wrap; gap: .5rem; margin-top: .75rem; }
.ef-tags--compact { margin-top: 0; }

.ef-tag {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  background: rgba(49,130,206,.1);
  color: #2b6cb0;
  border-radius: 20px;
  padding: 5px 12px;
  font-size: .825rem;
  font-weight: 600;
}
.ef-tag-icon { font-size: .7rem; }
.ef-tag-remove {
  background: none;
  border: none;
  cursor: pointer;
  color: #4299e1;
  padding: 0;
  line-height: 1;
  font-size: .7rem;
  margin-left: 2px;
}

/* ── Empty state ── */
.ef-empty-state {
  text-align: center;
  padding: 2.5rem 1rem;
  color: var(--gray-400);
}
.ef-empty-icon { font-size: 2.5rem; margin-bottom: .75rem; display: block; opacity: .35; }
.ef-empty-state p { font-size: 1rem; font-weight: 600; color: var(--gray-600); margin: 0 0 4px; }
.ef-empty-state span { font-size: .825rem; }

/* ── Product search ── */
.ef-product-search { position: relative; margin-bottom: 1.25rem; }
.ef-search-icon {
  position: absolute;
  left: 14px;
  top: 50%;
  transform: translateY(-50%);
  color: var(--gray-400);
  pointer-events: none;
}
.ef-search-input { padding-left: 38px; }
.ef-search-input--active {
  border-color: var(--blue);
  box-shadow: 0 0 0 3px rgba(49,130,206,.12);
  border-bottom-left-radius: 0;
  border-bottom-right-radius: 0;
}
.ef-search-icon--spin { animation: spin .7s linear infinite; color: var(--blue); }
@keyframes spin { to { transform: translateY(-50%) rotate(360deg); } }

.ef-dropdown {
  position: absolute;
  z-index: 9999;
  width: 100%;
  background: white;
  border: 1.5px solid var(--blue);
  border-top: none;
  border-radius: 0 0 10px 10px;
  box-shadow: 0 8px 24px rgba(0,0,0,.12);
  max-height: 260px;
  overflow-y: auto;
  margin-top: 0;
}
.ef-dropdown-loading { padding: 14px 16px; display: flex; flex-direction: column; gap: 10px; }
.ef-skeleton-row {
  height: 14px;
  background: linear-gradient(90deg, var(--gray-100) 25%, var(--gray-200) 50%, var(--gray-100) 75%);
  background-size: 200% 100%;
  border-radius: 6px;
  animation: shimmer 1.2s infinite;
}
.ef-skeleton-row--sm { width: 55%; height: 10px; }
@keyframes shimmer { 0% { background-position: 200% 0; } 100% { background-position: -200% 0; } }
.ef-dropdown-empty {
  padding: 20px 16px;
  text-align: center;
  color: var(--gray-400);
  font-size: .875rem;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
}
.ef-dropdown-empty .fa { font-size: 1.25rem; opacity: .5; }
.ef-dropdown-item {
  width: 100%;
  background: none;
  border: none;
  border-bottom: 1px solid var(--gray-100);
  padding: 10px 16px;
  text-align: left;
  cursor: pointer;
  transition: background .1s;
}
.ef-dropdown-item:last-child { border-bottom: none; }
.ef-dropdown-item:hover:not(.added) { background: var(--blue-light); }
.ef-dropdown-item.added { opacity: .55; cursor: default; }
.ef-dropdown-name { font-size: .875rem; font-weight: 600; color: var(--gray-800); }
.ef-dropdown-meta { font-size: .75rem; color: var(--gray-400); margin-top: 2px; display: flex; align-items: center; gap: 4px; }
.ef-dot { color: var(--gray-200); }
.ef-already-badge { background: #48bb78; color: white; border-radius: 10px; padding: 1px 8px; font-size: .7rem; margin-left: 4px; }

/* ── Product block ── */
.ef-product {
  border: 1.5px solid var(--gray-200);
  border-radius: 10px;
  margin-bottom: 1rem;
  overflow: hidden;
}

.ef-product-head {
  display: flex;
  align-items: center;
  gap: .75rem;
  padding: 12px 16px;
  background: var(--gray-50);
  border-bottom: 1px solid var(--gray-100);
}

.ef-product-avatar {
  width: 36px;
  height: 36px;
  border-radius: 8px;
  background: var(--purple-light);
  color: var(--purple);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.ef-product-info { flex: 1; min-width: 0; }
.ef-product-name { font-weight: 700; font-size: .9rem; color: var(--gray-800); display: block; }
.ef-product-id { font-size: .75rem; color: var(--gray-400); margin-left: 6px; }

.ef-icon-btn {
  width: 32px;
  height: 32px;
  border-radius: 8px;
  border: 1px solid transparent;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all .15s;
  font-size: .8rem;
  flex-shrink: 0;
  background: none;
}
.ef-icon-btn--danger { color: #e53e3e; border-color: #fed7d7; }
.ef-icon-btn--danger:hover { background: #fff5f5; }
.ef-icon-btn--ghost { color: var(--gray-400); }
.ef-icon-btn--ghost:hover { color: #e53e3e; background: #fff5f5; }

/* ── Rules ── */
.ef-rules { padding: 12px 16px; }
.ef-rules-title {
  display: flex;
  align-items: center;
  justify-content: space-between;
  font-size: .8rem;
  font-weight: 700;
  color: var(--gray-600);
  text-transform: uppercase;
  letter-spacing: .04em;
  margin-bottom: .75rem;
}

.ef-ghost-btn {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  background: none;
  border: 1px dashed var(--gray-200);
  border-radius: 6px;
  padding: 4px 10px;
  font-size: .78rem;
  color: var(--blue);
  cursor: pointer;
  transition: all .15s;
  font-weight: 600;
  text-transform: none;
  letter-spacing: 0;
}
.ef-ghost-btn:hover { background: var(--blue-light); border-color: var(--blue); }

.ef-rules-empty { font-size: .8rem; color: var(--gray-400); font-style: italic; margin: 0; }

.ef-rule {
  border: 1px solid var(--gray-200);
  border-radius: 8px;
  margin-bottom: .75rem;
  overflow: hidden;
}
.ef-rule-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 8px 12px;
  background: var(--gray-50);
  border-bottom: 1px solid var(--gray-100);
}
.ef-rule-num { font-size: .78rem; font-weight: 700; color: var(--gray-600); }
.ef-rule-body { padding: 12px; display: flex; flex-direction: column; gap: .75rem; }

.ef-rule-checks { display: flex; gap: 1.25rem; flex-wrap: wrap; }
.ef-check-label { display: flex; align-items: center; gap: 8px; font-size: .875rem; color: var(--gray-700); cursor: pointer; }
.ef-check { width: 16px; height: 16px; accent-color: var(--blue); cursor: pointer; }

.ef-char-count { font-size: .75rem; color: var(--gray-400); display: block; text-align: right; margin-top: 3px; }

/* ── Review ── */
.ef-review-section {
  border: 1px solid var(--gray-200);
  border-radius: 10px;
  margin-bottom: 1rem;
  overflow: hidden;
}
.ef-review-section-head {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 10px 16px;
  background: var(--gray-50);
  font-weight: 700;
  font-size: .85rem;
  color: var(--gray-700);
  border-bottom: 1px solid var(--gray-100);
  cursor: pointer;
}
.ef-review-section-head:hover { background: var(--gray-100); }
.ef-review-edit { font-size: .78rem; color: var(--blue); font-weight: 600; }

.ef-review-grid {
  display: flex;
  flex-wrap: wrap;
  gap: 0;
  padding: 12px 16px;
}
.ef-review-field { flex: 0 0 50%; padding: 6px 8px 6px 0; }
.ef-review-field--full { flex: 0 0 100%; }
.ef-review-key { font-size: .75rem; text-transform: uppercase; letter-spacing: .04em; color: var(--gray-400); display: block; margin-bottom: 2px; }
.ef-review-val { font-size: .875rem; color: var(--gray-800); font-weight: 500; }

.ef-review-product {
  padding: 10px 16px;
  border-bottom: 1px solid var(--gray-100);
}
.ef-review-product:last-child { border-bottom: none; }
.ef-review-product-name { font-size: .875rem; font-weight: 600; color: var(--gray-800); margin-bottom: 6px; }

.ef-review-rules { display: flex; flex-wrap: wrap; gap: .4rem; }
.ef-rule-chip {
  display: inline-flex;
  align-items: center;
  gap: 3px;
  background: var(--purple-light);
  color: var(--purple);
  font-size: .75rem;
  font-weight: 600;
  border-radius: 6px;
  padding: 3px 8px;
}
.ef-rule-req { color: #e53e3e; font-size: .8rem; }

.ef-review-empty { padding: 12px 16px; font-size: .875rem; color: var(--gray-400); font-style: italic; margin: 0; }

.ef-create-btn {
  width: 100%;
  padding: 16px;
  margin-top: 1.5rem;
  background: linear-gradient(135deg, #3182ce, #6b46c1);
  color: white;
  border: none;
  border-radius: 10px;
  font-size: 1rem;
  font-weight: 700;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all .2s;
  letter-spacing: .02em;
}
.ef-create-btn:hover:not(:disabled) { transform: translateY(-1px); box-shadow: 0 6px 20px rgba(49,130,206,.35); }
.ef-create-btn:disabled { opacity: .65; cursor: not-allowed; transform: none; }

/* ── Navigation ── */
.ef-nav {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 1.5rem;
}

.ef-nav-btn {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 12px 24px;
  border-radius: 10px;
  font-size: .9rem;
  font-weight: 600;
  cursor: pointer;
  transition: all .15s;
  border: 1.5px solid transparent;
}
.ef-nav-btn--secondary {
  background: white;
  border-color: var(--gray-200);
  color: var(--gray-600);
}
.ef-nav-btn--secondary:hover { border-color: var(--gray-400); color: var(--gray-800); }
.ef-nav-btn--primary {
  background: var(--blue);
  color: white;
  box-shadow: 0 4px 12px rgba(49,130,206,.25);
}
.ef-nav-btn--primary:hover { background: #2b77b8; transform: translateY(-1px); }

/* ── Error ── */
.ef-error {
  color: #e53e3e;
  font-size: .78rem;
  margin: 4px 0 0;
}
.mt-1 { margin-top: .25rem; }

/* ── Transition ── */
.slide-fade-enter-active, .slide-fade-leave-active { transition: all .2s ease; }
.slide-fade-enter { opacity: 0; transform: translateX(16px); }
.slide-fade-leave-to { opacity: 0; transform: translateX(-16px); }

/* ── Responsive ── */
@media (max-width: 640px) {
  .ef-wrapper { padding: 1rem .5rem 2rem; }
  .ef-topbar { flex-wrap: wrap; }
  .ef-step-info { display: none; }
  .ef-row { flex-direction: column; }
  .ef-field--half { flex: 1 0 100%; }
.ef-review-field { flex: 0 0 100%; }
}
</style>



// WEBPACK FOOTER //
// src/app/news_feed/views/CreateExecutionFocus.vue